import React from 'react';
import { ApolloQueryResult } from 'apollo-client';
import { useQuery } from '@apollo/react-hooks';

import customLocalStorage from 'Utils/customLocalStorage';

import ME_QUERY from './graphql/me.graphql';
import {
  MeAuthContext as MeAuthContextResponse,
  MeAuthContext_me as Me,
} from './graphql/types/MeAuthContext';

export type State = {
  data: {
    me?: Me | null;
    loading: boolean;
    error?: any;
    refetch: (variables?: {}) => Promise<
      ApolloQueryResult<MeAuthContextResponse>
    >;
    updateMe: (
      mapFn: (
        previousQueryResult: MeAuthContextResponse,
        options: {
          variables?: {};
        },
      ) => MeAuthContextResponse,
    ) => void;
  };
  logout: () => void;
  // TO IMPLEMENT
  // login
  // register
};

export const AuthContext = React.createContext<State | undefined>(undefined);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { data, loading, error, refetch, updateQuery } = useQuery<
    MeAuthContextResponse,
    {}
  >(ME_QUERY, { notifyOnNetworkStatusChange: true });

  React.useEffect(() => {
    // used in blog pages to refecth the user data after the user registration/login is completed.
    // With this we don't need to reload the entire page to see the changes of the user in the main menu
    window.getUserData = () => {
      refetch();
    };
  }, []);

  React.useEffect(() => {
    if (!loading && data) {
      if (data.me) {
        // set the user in sentry
        if (window.Sentry) {
          window.Sentry.configureScope(function(scope) {
            scope.setUser({
              email: data.me!.email,
              id: data.me!.originalId || undefined,
            });
          });
        }

        // custom data used in tracking services
        const customData = {
          segment: data.me.segmentationKey,
          username: data.me.username,
        };

        customLocalStorage.setItem(
          'creh-tracking-data',
          JSON.stringify(customData),
        );

        // redirect to homepage if the user is logged and we are in homepage
        if (window.PAGE_KEY === 'HOMEPAGE') {
          const url = data.me.organization
            ? `/org/${data.me.organization.slug}/`
            : '/home/';

          window.location.href = url;
        }
      } else if (window.Sentry) {
        window.Sentry.configureScope(function(scope) {
          scope.setUser({ email: null });
        });
      }
    }
  }, [loading, data]);

  const logout = () => {
    window.location.href = '/logout/';
    customLocalStorage.removeItem('creh-tracking-data');
    // TODO: review if user key is being used in old applications
    customLocalStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{
        data: {
          me: data?.me,
          loading,
          error,
          refetch,
          updateMe: updateQuery,
        },
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useMe = () => {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useMe must be used within an AuthProvider');
  }

  return context.data;
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
